import { useField, useFormikContext } from 'formik'
import _ from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { useNewTranslation } from 'hooks'
import React, { useEffect, useMemo, useState } from 'react'
import { TemplateFormContainer } from './style'
import { TaskElementProps } from './Actions'
import { TemplateDetailSchema } from '../TemplateDetail'
import { CampaignTaskElements } from 'services/api/campaignTemplates'
import OnFormikValueChange from '../OnFormikValueChange'
import { selectTemplateDetail, updateCampaignTaskElements, CampaignTemplateStep } from 'state/campaignTemplates'
import { selectCountrySetting } from 'state/constants'
import { CAMPAIGN_STATUS_ENUM, selectEditingCampaignCountry } from 'state/campaigns'
import { COUNTRY_ENUM } from 'services/api/constants'
import { selectTemplateFirstStepDataByTaskIndex } from 'state/appPreview/selector'
import { formatTaskValues, interpolateAndJoin } from 'components/utils/interpolation'
import { updatePurchaseStep1Data, updatePurchaseInterpolationListData } from 'state/appPreview/action/purchase'
import TemplateTask from './TemplateTask'
import { fieldKeyByTasks } from '../TemplateDetail/options'
import { formatCurrencyNumberInput } from 'components/utils'
import { selectCampaignByTemplateData } from 'state'

const fieldNames = fieldKeyByTasks.purchase

const PurchaseTask = ({
    showError: baseShowError,
    taskOrder,
    updateCurrentStepStatus,
    setNavigationDisable,
    isReadOnly,
    ...props
}: TaskElementProps) => {
    const [t] = useNewTranslation()
    const { values, setFieldValue } = useFormikContext<TemplateDetailSchema>()
    const [showError, setShowError] = useState(baseShowError)
    const dispatch = useDispatch()
    const campaign = useSelector(selectCampaignByTemplateData)
    const templateDetail = useSelector(selectTemplateDetail)
    const selectedTemplateFirstStepData = useSelector(selectTemplateFirstStepDataByTaskIndex(taskOrder))
    const countrySetting = useSelector(selectCountrySetting((templateDetail?.country as COUNTRY_ENUM) || null))
    const editingCampaignCountry = useSelector(selectEditingCampaignCountry)
    const [minPurchaseQuantity] = useField(`task_elements.${taskOrder}.min_purchase_quantity`)
    const productsValue = values.task_elements[taskOrder].products
    const isCampaignLive = campaign?.status === CAMPAIGN_STATUS_ENUM.LIVE

    const namePrefix = `task_elements.${taskOrder}.`
    const validateFieldsKey = fieldNames.map(field => namePrefix + field) as (keyof TemplateDetailSchema)[]
    const disableAutoSave = () => {
        // Enable data capture and preview update if campaign is live
        if (isCampaignLive) {
            return false
        }

        return isReadOnly
    }

    useEffect(() => {
        if (minPurchaseQuantity.value === 0) {
            setFieldValue(`task_elements.${taskOrder}.min_purchase_quantity`, 1)
        }

        if (productsValue && productsValue?.length < minPurchaseQuantity.value) {
            setFieldValue(`task_elements.${taskOrder}.min_purchase_quantity`, productsValue.length)
        }
    }, [minPurchaseQuantity, productsValue?.length])

    useEffect(() => {
        handleDebouncedChange(values)
    }, [])

    useEffect(() => {
        setShowError(baseShowError)
    }, [baseShowError])

    const onSaveData = (fieldsValues: Partial<TemplateDetailSchema>, showSuccessToast: boolean) => {
        const validTemplateDataUuid = typeof props.templateUuid === 'string' ? props.templateUuid : ''
        const purchaseTaskValues = (fieldsValues?.task_elements?.[taskOrder] ?? {}) as CampaignTaskElements
        dispatch(
            updateCampaignTaskElements({
                templateUuid: validTemplateDataUuid,
                ...purchaseTaskValues,
                setNavigationDisable: setNavigationDisable,
                showSuccessToast,
            }),
        )
    }

    const handleDebouncedChange = (fieldsValues: Partial<TemplateDetailSchema>) => {
        const purchaseTaskValues = (fieldsValues?.task_elements?.[taskOrder] ?? {}) as CampaignTaskElements
        const isShippingFeeValid =
            typeof purchaseTaskValues.shipping_fee === 'number' && purchaseTaskValues.shipping_fee >= 0
        if (!isShippingFeeValid) {
            delete purchaseTaskValues.shipping_fee
        }
        if (purchaseTaskValues.element) {
            const howToBuy = [...(selectedTemplateFirstStepData.step?.how_to_buy ?? [])]
            if (howToBuy && purchaseTaskValues?.products) {
                let productValues = ''
                let itemsToReview: string[] = []
                const min_purchase_quantity = purchaseTaskValues?.min_purchase_quantity?.toString() ?? ''
                const shipping_fee = purchaseTaskValues?.shipping_fee
                const purchaseLocationValues = formatTaskValues(purchaseTaskValues?.purchase_locations, {
                    isList: true,
                    bulletPoint: '\u002D',
                })

                if (
                    purchaseTaskValues?.products.length > 1 ||
                    !_.isEmpty(purchaseTaskValues?.products[0]?.value?.name) ||
                    !_.isEmpty(purchaseTaskValues?.products[0]?.value?.price)
                ) {
                    productValues = purchaseTaskValues?.products.reduce((accumulator, product, index, array) => {
                        const formattedString = `${index + 1}.  ${product.value.name} - ${budgetCurrencySign} ${product.value.price ?? ''}`
                        const isLastItem = index === array.length - 1
                        return accumulator + (isLastItem ? formattedString : formattedString + '\n')
                    }, '')
                    itemsToReview = purchaseTaskValues?.products.map(product => product.value.name) // store purchaseTaskValues with itemsToReview format for Review task to used in app preview
                }

                const keyValueMap = [
                    {
                        key: 'min_purchase_quantity',
                        value: min_purchase_quantity,
                        emptyValue: 'ANY',
                    },
                ]

                const interpolatedString = interpolateAndJoin(howToBuy, { keyValueMap })
                const payload = {
                    description: interpolatedString,
                    examplePhotos: purchaseTaskValues?.example_medias,
                    purchaseLocations: purchaseTaskValues?.purchase_locations,
                    itemsToReview: itemsToReview,
                }
                const interpolationListPayload = {
                    howToBuy: {
                        template: interpolatedString,
                        values: [
                            {
                                purchase_locations: purchaseLocationValues,
                                emptyValue: '\u002D ' + t('campaign.template.purchaseLocation.placeholder.text'),
                            },
                            {
                                products: productValues,
                                emptyValue: '\u002D ' + t('campaign.template.productDetails.column1.placeholder.text'),
                            },
                            {
                                shipping_fee:
                                    typeof shipping_fee === 'number'
                                        ? formatCurrencyNumberInput({
                                              value: shipping_fee,
                                              country: templateDetail?.country as COUNTRY_ENUM,
                                              currencySign: budgetCurrencySign,
                                          })
                                        : undefined,
                                emptyValue: t('campaign.template.shippingFee.appPreview.placeholder'),
                                isInline: true,
                            },
                        ],
                    },
                }

                dispatch(updatePurchaseStep1Data(payload))
                dispatch(updatePurchaseInterpolationListData(interpolationListPayload))
            }

            if (!isCampaignLive && !isReadOnly) {
                onSaveData(fieldsValues, false)
            }
        }
    }
    const budgetCurrencySign = useMemo(() => {
        if (!countrySetting) return editingCampaignCountry
        return countrySetting.currency.sign
    }, [editingCampaignCountry, countrySetting])

    const onShowError = () => {
        setShowError(true)
    }

    return (
        <TemplateFormContainer data-testid="test-purchase-task-page">
            <OnFormikValueChange
                callback={handleDebouncedChange}
                fields={validateFieldsKey}
                skipCallbackError={{}}
                validateSpecificField={props.validateSpecificField}
                showError={showError}
                updateCurrentStepStatus={updateCurrentStepStatus}
                // Only disableNavigation for input if campaign is not live
                {...(!isCampaignLive && { setDisableNavigation: setNavigationDisable })}
                disableAutoSave={disableAutoSave()}
            />
            <TemplateTask
                elementKey="purchase"
                field_keys={fieldNames}
                taskOrder={taskOrder}
                updateCurrentStepStatus={updateCurrentStepStatus}
                showError={showError}
                identifierPrefix={CampaignTemplateStep.PURCHASE}
                onSaveData={onSaveData}
                onShowError={onShowError}
                fields={validateFieldsKey}
                {...props}
            />
        </TemplateFormContainer>
    )
}

export default PurchaseTask
